import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { normalizeBrandDomain } from '@rock/brands';
import type { Brand } from '@rock/types';
import { BadgeCheck } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { BRAND_DOMAIN_SOURCE_LABEL, buildBrandDomainRows } from './brandDomainRows';

/** The two lookups that aren't one of the brand's own domains. */
const NAME_CHOICE = 'name';
const CUSTOM_CHOICE = 'custom';
/** Domain choices are namespaced so no hostname can ever collide with the two sentinels above. */
const DOMAIN_PREFIX = 'domain:';

const domainChoice = (domain: string): string => `${DOMAIN_PREFIX}${domain}`;

interface BrandfetchDialogProps {
    brand: Brand;
    open: boolean;
    isEnriching: boolean;
    onOpenChange: (open: boolean) => void;
    onFetch: (domain?: string) => Promise<void>;
}

/**
 * Picks WHAT Brandfetch is asked about before spending the call. A name search resolves to whatever
 * company Brandfetch thinks owns the name and then overwrites `website`, `brandfetch.domain`,
 * description and the non-manual logo slots — which silently discarded the domain an admin had
 * deliberately fetched from. So a domain is always the default when the brand has one (the last-fetched
 * one first, else its leading known domain), and searching by name is the deliberate choice rather than
 * the accidental one — it's only pre-selected for a brand with no domain at all.
 */
export const BrandfetchDialog: FC<BrandfetchDialogProps> = ({ brand, open, isEnriching, onOpenChange, onFetch }) => {
    const rows = useMemo(() => buildBrandDomainRows(brand), [brand]);
    const lastDomain = brand.brandfetch ? normalizeBrandDomain(brand.brandfetch.domain) : null;
    const lastFetchedLabel = brand.brandfetch ? new Date(brand.brandfetch.fetchedAt).toLocaleDateString() : '';
    const preferredDomain = lastDomain ?? rows[0]?.domain ?? null;
    const defaultChoice = preferredDomain ? domainChoice(preferredDomain) : NAME_CHOICE;

    const [choice, setChoice] = useState<string>(defaultChoice);
    const [customInput, setCustomInput] = useState('');

    // Re-arm on every open — a brand fetched since the last open has a different safe default.
    useEffect(() => {
        if (!open) return;
        setChoice(defaultChoice);
        setCustomInput('');
    }, [open, defaultChoice]);

    const customDomain = normalizeBrandDomain(customInput);
    const isCustomUnusable = !!customInput.trim() && !customDomain;
    const canFetch = !isEnriching && (choice !== CUSTOM_CHOICE || !!customDomain);

    const handleFetch = async () => {
        if (!canFetch) return;
        let domain: string | undefined;
        if (choice === CUSTOM_CHOICE) {
            // An unusable custom domain BAILS rather than falling through to `undefined` — silently
            // degrading to the by-name search is the exact accident this dialog exists to prevent.
            if (!customDomain) return;
            domain = customDomain;
        } else if (choice !== NAME_CHOICE) {
            domain = choice.slice(DOMAIN_PREFIX.length);
        }
        await onFetch(domain);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isEnriching && onOpenChange(isOpen)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Fetch from Brandfetch</DialogTitle>
                    <DialogDescription>
                        Choose what to look up. Whatever answers becomes this brand’s website, description and logos —
                        hand-assigned assets are never overwritten.
                    </DialogDescription>
                </DialogHeader>

                <RadioGroup
                    className="py-2"
                    value={choice}
                    onValueChange={setChoice}
                    disabled={isEnriching}
                    aria-label="Brandfetch lookup"
                >
                    <ChoiceRow
                        value={NAME_CHOICE}
                        id="brandfetch-choice-name"
                        selected={choice === NAME_CHOICE}
                        disabled={isEnriching}
                    >
                        <span className="text-sm font-medium text-foreground">
                            Search by name — “{brand.canonicalName}”
                        </span>
                        <span className="text-xs text-muted-foreground">
                            Resolves to whatever company Brandfetch matches; can replace the domain below.
                        </span>
                    </ChoiceRow>

                    {rows.map((row) => (
                        <ChoiceRow
                            key={row.domain}
                            value={domainChoice(row.domain)}
                            id={`brandfetch-choice-${row.domain}`}
                            selected={choice === domainChoice(row.domain)}
                            disabled={isEnriching}
                        >
                            <span className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-sm font-medium text-foreground">{row.domain}</span>
                                <Badge variant="secondary">{BRAND_DOMAIN_SOURCE_LABEL[row.source]}</Badge>
                                {row.domain === lastDomain ? (
                                    <span className="flex items-center gap-1 text-xs text-emerald-600">
                                        <BadgeCheck className="h-4 w-4 shrink-0" aria-hidden />
                                        Last fetched {lastFetchedLabel}
                                    </span>
                                ) : null}
                            </span>
                        </ChoiceRow>
                    ))}

                    <ChoiceRow
                        value={CUSTOM_CHOICE}
                        id="brandfetch-choice-custom"
                        selected={choice === CUSTOM_CHOICE}
                        disabled={isEnriching}
                    >
                        <span className="text-sm font-medium text-foreground">Another domain</span>
                        {/* Focus/typing — NOT the surrounding label — is what selects this row: the HTML spec
                            skips label activation for clicks on interactive descendants like this input. */}
                        <Input
                            className="mt-1"
                            aria-label="Custom domain"
                            placeholder="e.g. delonghi.com"
                            value={customInput}
                            onChange={(e) => {
                                setCustomInput(e.target.value);
                                setChoice(CUSTOM_CHOICE);
                            }}
                            onFocus={() => setChoice(CUSTOM_CHOICE)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') void handleFetch();
                            }}
                            disabled={isEnriching}
                        />
                        {isCustomUnusable ? (
                            <span className="text-xs text-destructive">
                                Not a usable domain — a hostname needs a dot, e.g. delonghi.com.
                            </span>
                        ) : null}
                    </ChoiceRow>
                </RadioGroup>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isEnriching}>
                        Cancel
                    </Button>
                    <Button onClick={() => void handleFetch()} disabled={!canFetch}>
                        {isEnriching ? 'Fetching…' : 'Fetch'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

/** One selectable lookup: the radio, then the row's content as its label. */
const ChoiceRow: FC<{
    value: string;
    id: string;
    selected: boolean;
    disabled: boolean;
    children: ReactNode;
}> = ({ value, id, selected, disabled, children }) => (
    <div className={`flex items-start gap-3 rounded-md border p-3 ${selected ? 'border-primary' : ''}`}>
        <RadioGroupItem value={value} id={id} className="mt-0.5" />
        <label htmlFor={id} className={`flex min-w-0 flex-1 flex-col gap-0.5 ${disabled ? '' : 'cursor-pointer'}`}>
            {children}
        </label>
    </div>
);
